import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from 'react'
import { HubConnectionBuilder, HubConnection, LogLevel } from '@microsoft/signalr'
import {
  postConfirmUserNotification,
  postMessageRead,
  getListMessage
} from 'services/notification'
import { dynamicSort } from 'commons/utils/array'
import {
  NotificationType,
  Source
} from 'components/Structures/Header/Notifications/Notifications'

interface TransactionProviderProps {
  children: ReactNode
}

export interface Notification {
  id: number
  source: Source
  title: string
  subtitle?: string
  message: string
  notes: string
  type: NotificationType
  read: boolean
  create_date: string
  priority?: number
  notificationId?: string
}

interface NotificationsContextProps {
  notificationsData: Notification[]
  setNotificationsData: Dispatch<SetStateAction<Notification[]>>
  unreadedNotificationsData: Notification[]
  setUnreadedNotificationsData: Dispatch<SetStateAction<Notification[]>>
  processUnreadedNotifications: () => void
}

export const NotificationsContext = createContext<NotificationsContextProps>(
  {} as NotificationsContextProps
)

export function NotificationsProvider({ children }: TransactionProviderProps) {
  const [connection, setConnection] = useState<HubConnection | null>(null)
  const [initialSteps, setInitialSteps] = useState<{
    hasInitialDefinition: boolean
    hasFirstLoading: boolean
    hasNotificationProcessing: boolean
  }>({
    hasInitialDefinition: false,
    hasFirstLoading: false,
    hasNotificationProcessing: false
  })
  const [notificationsData, setNotificationsData] = useState<Array<Notification>>([])
  const [unreadedNotificationsData, setUnreadedNotificationsData] = useState<
    Array<Notification>
  >([])
  const [notificationsIds, setNotificationsIds] = useState<Array<number>>([])

  const getTokenAndUser = (): Array<string> => {
    let loginToken = ''
    if (localStorage.token) {
      const data = JSON.parse(localStorage.token)
      loginToken = data.accessToken
    }
    let userCode = ''
    if (localStorage.cd_usua) {
      const data = JSON.parse(localStorage.cd_usua)
      userCode = data.cd_usua
    }
    return [loginToken, userCode]
  }

  const getListNotificationsData = async () => {
    const data: Array<Notification> | void = await getListMessage()
    if (Array.isArray(data)) {
      const unread: Array<Notification> = data.filter(
        (notification: Notification) => notification.read === false
      )
      setUnreadedNotificationsData([...unread].reverse())
      const dataReversed = [...data].reverse()
      const newNotificationsIds = dataReversed.map(
        (notification: Notification) => notification.id
      )
      setNotificationsIds(newNotificationsIds)
      setNotificationsData(dataReversed)
    }
  }

  useEffect(() => {
    const baseURL = process.env.REACT_APP_BASE_URL
    const [loginToken, userCode] = getTokenAndUser()
    if (loginToken) {
      getListNotificationsData()
      const newConnection = new HubConnectionBuilder()
        .withUrl(baseURL + '/home-notificacao/' + userCode.toUpperCase(), {
          accessTokenFactory: () => loginToken,
          headers: {
            'Ocp-Apim-Subscription-Key': `${process.env.REACT_APP_BASE_SUBSCRIPTION}`
          }
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect()
        .build()
      setConnection(newConnection)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const insertOnDatas = (notification: Notification) => {
    const newUnreadData = [...unreadedNotificationsData]
    newUnreadData.unshift(notification)
    setUnreadedNotificationsData(newUnreadData)
    const newNotificationsIds = [...notificationsIds]
    newNotificationsIds.unshift(notification.id)
    setNotificationsIds(newNotificationsIds)
    const newNotificationsData = [...notificationsData]
    newNotificationsData.unshift(notification)
    const sortNewNotificationsData = [...newNotificationsData].sort(dynamicSort('id'))
    setNotificationsData([...sortNewNotificationsData].reverse())
  }

  const realizeConnection = () => {
    if (connection) {
      connection.on('newMessage', async (message) => {
        const newMessage = JSON.parse(message)
        const newNotification: Notification = JSON.parse(newMessage.message)
        if (!notificationsIds.includes(newNotification.id)) {
          connection.off('newMessage')
          postConfirmUserNotification({
            notificationId: newMessage.notificationId
          })
          insertOnDatas(newNotification)
        } else if (!initialSteps.hasNotificationProcessing) {
          postConfirmUserNotification({
            notificationId: newMessage.notificationId
          })
        }
      })
    }
  }

  useEffect(() => {
    if (connection && initialSteps.hasFirstLoading) {
      if (!initialSteps.hasNotificationProcessing) {
        connection
          .start()
          .then(() => {
            realizeConnection()
          })
          .catch((e) => {
            console.error('Connection failed: ', e)
          })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, initialSteps])

  const processUnreadedNotifications = () => {
    unreadedNotificationsData.forEach((element: Notification) => {
      postMessageRead({ id_noti: element.id })
    })
    const newNotificationsData = [...notificationsData]
    unreadedNotificationsData.forEach((notification: Notification) => {
      newNotificationsData[notificationsData.indexOf(notification)] = {
        ...notification,
        read: true
      }
    })
    setNotificationsData(newNotificationsData)
    setUnreadedNotificationsData([])
  }

  useEffect(() => {
    if (!initialSteps.hasInitialDefinition) {
      setInitialSteps({
        ...initialSteps,
        hasInitialDefinition: true
      })
    } else if (!initialSteps.hasFirstLoading) {
      setInitialSteps({
        ...initialSteps,
        hasFirstLoading: true
      })
    } else if (!initialSteps.hasNotificationProcessing) {
      setInitialSteps({
        ...initialSteps,
        hasNotificationProcessing: true
      })
      realizeConnection()
    } else {
      realizeConnection()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsData])

  return (
    <NotificationsContext.Provider
      value={{
        notificationsData,
        setNotificationsData,
        unreadedNotificationsData,
        setUnreadedNotificationsData,
        processUnreadedNotifications
      }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotification() {
  const context = useContext(NotificationsContext)
  return context
}

export default useNotification
